/**
 * ExportsCard — Settings → Exports (§31 E-1): the reader for the export
 * audit, GET /v1/exports. A platform record, not a Sales one — 0128 is the
 * table every export surface will write to — so it lives under the tenant's
 * own gear, a sibling of Company and Sales, and does not move when the
 * second surface lands.
 *
 * E-2  the gate is the SENTENCE, with no request: a non-owner/admin (or a
 *      session whose /v1/me has not resolved — fail closed) is told the
 *      record exists and is not theirs to read, the way the approvals drawer
 *      swaps its buttons for a sentence. The server's 403 stays the boundary;
 *      this card just never asks a question it knows the answer to.
 * E-5  the definition is a sentence from the builder's own vocabulary; the
 *      raw JSON sits behind a disclosure on every row and is the ONLY render
 *      when the version is one this bundle does not speak.
 * E-6  "rows in the file", never records; the record count reads "not
 *      recorded" when the wire sent null — the state both live rows are in.
 * E-7  an open row reads "Never confirmed complete" with its sentence.
 * E-8  pages by the wire's cursor; "Showing N of Total" from page.total.
 *
 * Reader-grade in the table: who, when (date and time), what (the
 * sentence), rows in the file, status. Operator-grade behind the row's
 * disclosure: records, excluded, bytes, id, definition version, the JSON.
 */
import { useState } from 'react'
import { LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchDataExports } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import type { DataExport } from '../../../../api/types'
import type { MeResponse } from '../../types'
import {
  EXPORTS_PAGE, EXPORTS_GATE_SENTENCE, canReadExports, exportActor, exportStatus, STATUS_LABEL, UNCONFIRMED_NOTE,
  rowsInFile, recordCountText, excludedText, bytesText, surfaceLabel, definitionSentence, definitionJSON,
  showingLine, appendPage,
} from './exports'
import { fmtDateTime } from '../../api'
import { danger } from '../../../../theme/tokens'

export function ExportsCard({ me }: { me: Pick<MeResponse, 'role'> | null }) {
  return (
    <div className="k-card">
      <div className="p-5 flex flex-col gap-4">
        <div>
          <div className="text-13 font-semibold text-ink-primary">Export records</div>
          <div className="text-12 text-ink-muted mt-1">
            Every export from this workspace is recorded before its first byte leaves — who asked, what was asked and when — and completed after the last. Records are never deleted.
          </div>
        </div>
        {canReadExports(me)
          ? <ExportsList />
          : <div className="text-12 text-ink-muted" data-exports-gate>{EXPORTS_GATE_SENTENCE}</div>}
      </div>
    </div>
  )
}

function ExportsList() {
  const first = useAsync(() => fetchDataExports({ limit: EXPORTS_PAGE }), [])
  // Pages loaded beyond the first, and the cursor that reaches the next one.
  // The first page's own cursor is read from the fetch until "Load older"
  // has been pressed once; after that the appended state owns it.
  const [more, setMore] = useState<{ rows: DataExport[]; cursor: string | undefined } | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [moreError, setMoreError] = useState<string | null>(null)

  if (first.loading) return <LoadingState rows={3} />
  if (first.error) return <ErrorState message={first.error} />
  const page = first.data!
  const rows = more ? more.rows : page.items
  const cursor = more ? more.cursor : page.page.nextCursor
  const total = page.page.total

  async function loadOlder() {
    if (!cursor || loadingMore) return
    setLoadingMore(true); setMoreError(null)
    try {
      const next = await fetchDataExports({ limit: EXPORTS_PAGE, cursor })
      setMore({ rows: appendPage(rows, next.items), cursor: next.page.nextCursor })
    } catch (e) {
      setMoreError(apiErrorMessage(e, 'Failed to load older exports'))
    } finally {
      setLoadingMore(false)
    }
  }

  if (rows.length === 0) {
    return <EmptyState message="No exports yet" hint="The first CSV export from the Reports Builder will be recorded here." />
  }

  const line = showingLine(rows.length, total)
  return (
    <div className="flex flex-col gap-3">
      <table className="dash-table w-full" aria-label="Export records">
        <thead>
          <tr>
            <th className="text-left">Who</th>
            <th className="text-left">When</th>
            <th className="text-left">What</th>
            <th className="text-right">File</th>
            <th className="text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => <ExportRow key={r.id} row={r} />)}
        </tbody>
      </table>
      {line && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-12 text-ink-muted" data-exports-showing>{line}</span>
          <button type="button" className="k-btn-secondary" disabled={loadingMore} onClick={() => void loadOlder()}>
            {loadingMore ? 'Loading…' : 'Load older exports'}
          </button>
        </div>
      )}
      {moreError && <div className="text-12 font-medium" role="alert" style={{ color: danger.text }}>{moreError}</div>}
    </div>
  )
}

function ExportRow({ row }: { row: DataExport }) {
  const status = exportStatus(row)
  const sentence = definitionSentence(row.definition, row.definitionVersion)
  return (
    <tr data-export-id={row.id}>
      <td className="text-13 text-ink-primary font-medium whitespace-nowrap" title={row.userId ? `User ${row.userId}` : 'The user record no longer exists'}>{exportActor(row)}</td>
      <td className="text-12 text-ink-muted whitespace-nowrap">{fmtDateTime(row.startedAt)}</td>
      <td className="text-12 text-ink-body">
        <div>{surfaceLabel(row.surface)}</div>
        {sentence !== null
          ? <div className="text-ink-muted">{sentence}</div>
          : <div className="text-ink-muted">Definition version {row.definitionVersion} — this screen cannot put it in words; the JSON below is the record.</div>}
        <details className="mt-1">
          <summary className="k-eyebrow uppercase cursor-pointer">Details</summary>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-12 text-ink-muted">
            <dt>Records</dt><dd>{recordCountText(row)}</dd>
            <dt>Excluded</dt><dd>{excludedText(row)}</dd>
            <dt>Bytes</dt><dd>{bytesText(row)}</dd>
            <dt>Export id</dt><dd className="font-mono">{row.id}</dd>
            <dt>Definition version</dt><dd>{row.definitionVersion}</dd>
            <dt>Started at</dt><dd>{row.startedAt}</dd>
            <dt>Completed at</dt><dd>{row.completedAt ?? '—'}</dd>
          </dl>
          <pre className="mt-2 text-11 font-mono text-ink-muted whitespace-pre-wrap">{definitionJSON(row.definition)}</pre>
        </details>
      </td>
      <td className="text-12 text-ink-muted text-right whitespace-nowrap">{rowsInFile(row)}</td>
      <td className="text-12">
        <div className={status === 'completed' ? 'text-ink-body' : 'text-ink-primary font-medium'}>{STATUS_LABEL[status]}</div>
        {status === 'unconfirmed' && <div className="text-ink-muted">{UNCONFIRMED_NOTE}</div>}
      </td>
    </tr>
  )
}
